import type { EmitActionParams } from '@unifyapps/defs/hooks/useBlockAction';
import type { TriggerEventParams } from '@unifyapps/defs/hooks/useBlockEvents';
import type { EventHandlerObserver } from '../event/observer';
import { isErrorsOn, isPulseOn, pulse } from './floor';
import type { EntityRefLite, ScopeId } from './types';

const EMPTY_SCOPE = '' as ScopeId;

const blockSource = (blockId?: string): EntityRefLite | undefined =>
  blockId ? { type: 'block', id: blockId } : undefined;

/**
 * Bridges the event handler's neutral lifecycle callbacks to the Pulse floor. Every bit of Pulse
 * knowledge — scope resolution, payload shaping, on/off gating — lives here so NoCodeEventHandler
 * stays a pure event runner. Mirrors DataSourcePulseObserver: the core stays Pulse-agnostic and the
 * observer plugs the tracking in from outside.
 */
export function createEventPulseObserver(
  resolveScope: (instanceId?: string) => ScopeId,
): EventHandlerObserver {
  return {
    onEvent(params: TriggerEventParams) {
      if (!isPulseOn()) return;
      const { actionContext, eventPayload, eventType } = params;
      // most blocks carry their event data in actionContext (e.g. { value }, { clickedAttachment });
      // a few pass eventPayload. capture both so the row shows what the event actually fired with —
      // a plain object only (actionContext can be a function, which has nothing to snapshot).
      const contextData =
        actionContext && typeof actionContext === 'object'
          ? (actionContext as Record<string, unknown>)
          : undefined;
      const eventData =
        eventPayload && typeof eventPayload === 'object'
          ? (eventPayload as Record<string, unknown>)
          : undefined;
      const payload =
        contextData || eventData ? { ...contextData, ...eventData, eventType } : undefined;
      pulse.event(
        resolveScope(params.instanceId),
        eventType,
        payload,
        blockSource(params.blockId ?? undefined),
        params.label,
      );
    },

    onActionStart(params, trigger) {
      if (!isPulseOn()) return;
      const { action } = params;
      pulse.action(
        resolveScope(params.instanceId),
        action.actionType ?? '',
        { status: 'ok' },
        action.executionType,
        blockSource(params.blockId ?? undefined),
        trigger,
        action.payload,
      );
    },

    onActionSkipped(params, reason, trigger) {
      if (!isPulseOn()) return;
      const { action } = params;
      pulse.action(
        resolveScope(params.instanceId),
        action.actionType ?? '',
        { status: 'skipped', reason },
        action.executionType,
        blockSource(params.blockId ?? undefined),
        trigger,
        action.payload,
      );
    },

    onActionError(params: EmitActionParams, error: unknown) {
      const { action } = params;
      // errors-on path emits independently of pulse-on (floor gates pulse.error on ERRORS_ON too),
      // so resolve the real scope whenever either gate is live — otherwise errors land with scope=''.
      const scope = isPulseOn() || isErrorsOn() ? resolveScope(params.instanceId) : EMPTY_SCOPE;
      const actionType = action.actionType ?? '';
      pulse.error(scope, 'action', String((error as Error)?.message ?? error), {
        expression: actionType,
        stack: (error as Error)?.stack?.slice(0, 2000),
        entity: { type: 'action', id: actionType },
      });
    },
  };
}
